<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { type ControlSize, control } from "@/design/control";
import Field from "./Field.vue";
import Icon from "./Icon.vue";

/**
 * Uma linha de entrada, dentro do envelope de campo.
 *
 * A altura vem do `control.ts` e o padrão é o denso: enquanto ela era `h-10`
 * fixa, esta primitiva aparecia em 13 arquivos e 179 controles eram escritos à
 * mão com `h-8` — ela não era ignorada por preguiça, ela não cabia. Quem quer o
 * campo grande (as telas de entrar e criar conta) pede `size="md"`.
 *
 * Tudo que não é prop cai no `<input>`, não no envelope: `inputmode`, `step`,
 * `maxlength` e `@blur` pertencem ao controle.
 */
defineOptions({ inheritAttrs: false });

const props = defineProps<{
	label?: string;
	modelValue: string;
	type?:
		| "text"
		| "email"
		| "password"
		| "search"
		| "tel"
		| "url"
		| "number"
		| "date"
		| "month"
		| "time"
		| "datetime-local";
	size?: ControlSize;
	placeholder?: string;
	hint?: string;
	error?: string;
	/** Inválido sem mensagem própria — o erro está sendo desenhado por fora. */
	invalid?: boolean;
	describedBy?: string;
	id?: string;
	autocomplete?: string;
	required?: boolean;
	disabled?: boolean;
	class?: string;
}>();
const emit = defineEmits<{ "update:modelValue": [string] }>();

const { t } = useI18n({ useScope: "global" });

/**
 * O OLHO DA SENHA — e por que ele mora na primitiva.
 *
 * Uma senha escrita às cegas é um erro de digitação que só aparece no "não
 * confere", e em sete telas desta casa (entrar, criar conta, aceitar convite,
 * repor a senha, a conta, um registo partilhado, um documento) o campo é o
 * mesmo componente. Escrever o botão em cada uma seria a mesma peça sete vezes,
 * e a sétima ficaria por fazer.
 *
 * Só existe quando `type` é `password`: um olho num campo de e-mail é um botão
 * que não faz nada. O tipo do `<input>` é o que muda — nunca a fonte ou um
 * truque de CSS —, porque é ele que diz ao gestor de senhas do navegador e ao
 * leitor de ecrã o que aquilo é.
 *
 * O estado morre com a tela: quem volta a esta página escreve outra vez, e
 * lembrar-se de "estava visível" é lembrar-se da coisa errada.
 */
const isPassword = computed(() => props.type === "password");
const revealed = ref(false);
const inputType = computed(() =>
	isPassword.value ? (revealed.value ? "text" : "password") : (props.type ?? "text"),
);

const classes = computed(() =>
	twMerge(control({ size: props.size }), isPassword.value ? "pr-10" : "", props.class),
);
</script>

<template>
	<Field
		v-slot="{ id: controlId, describedBy: describes, invalid: wrong }"
		:label="label"
		:hint="hint"
		:error="error"
		:required="required"
		:invalid="invalid"
		:described-by="describedBy"
		:id="id"
	>
		<div class="relative">
			<!-- `@change` além de `@input` (30/09): o Safari do iPhone entrega o valor de
			     `date`/`time`/`datetime-local` no `change`, ao fechar a roda — só com
			     `@input` o modelo ficava vazio e o formulário gravava sem a data. -->
			<input
				:id="controlId"
				:type="inputType"
				:value="modelValue"
				:placeholder="placeholder"
				:autocomplete="autocomplete"
				:required="required"
				:disabled="disabled"
				:aria-invalid="wrong"
				:aria-describedby="describes"
				:class="classes"
				v-bind="$attrs"
				@input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
				@change="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
			/>

			<!-- `tabindex="-1"` de propósito: quem escreve a senha e carrega em Tab
			     quer o botão de entrar, não o olho. Quem o quer alcança-o pelo rato ou
			     pelos atalhos do leitor de ecrã, que leem o `aria-label`. -->
			<button
				v-if="isPassword"
				type="button"
				tabindex="-1"
				class="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-xl text-fg-subtle transition-colors hover:text-fg disabled:opacity-40"
				:disabled="disabled"
				:aria-label="revealed ? t('field.hidePassword') : t('field.showPassword')"
				:aria-pressed="revealed"
				@click="revealed = !revealed"
			>
				<Icon :name="revealed ? 'eyeOff' : 'eye'" class="size-4" />
			</button>
		</div>
	</Field>
</template>
